import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Boxes, Briefcase, Database, FolderTree, Layers, Lock, ShieldCheck, Sigma } from 'lucide-react';
import PortfolioManagementScreen from './PortfolioManagementScreen';
import SecurityManagementScreen from './SecurityManagementScreen';
import ComplianceRulesScreen from './ComplianceRulesScreen';
import PortfolioGroupsScreen from './PortfolioGroupsScreen';
import StructuredProductsScreen from './reference/StructuredProductsScreen';
import CreditLoanReferenceWorkspace from './reference/CreditLoanReferenceWorkspace';
import RetailReferenceScreen from './retail/RetailReferenceScreen';
import { useTenantConfig } from '../contexts/TenantConfigContext';
import { AxiomMetricCard, AxiomMetricGrid, AxiomStatusBadge } from './shared';
import {
  CollapsedRail,
  CollapsedRailItem,
  CollapsedRailSection,
  Eyebrow,
  HeroSubtitle,
  HeroTitle,
  HeroTop,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  RailItem,
  RailItemDescription,
  RailItemLabel,
  RailSection,
  RailSectionLabel,
  WorkspaceBody,
  WorkspaceHero,
  WorkspaceMain,
  WorkspacePanel,
  WorkspaceRail,
  WorkspaceRailToggle,
  WorkspaceShell,
} from './shared/Workspace';

type TabType = 'portfolios' | 'groups' | 'securities' | 'structured' | 'derivatives' | 'credit-loans' | 'compliance';

const ReferenceDataScreen: React.FC = () => {
  const location = useLocation();
  const { isRetail } = useTenantConfig();
  const [activeTab, setActiveTab] = useState<TabType>('portfolios');
  const [railCollapsed, setRailCollapsed] = useState(false);

  // Deep-link from a thin ticket's "Define new instrument" action:
  // /reference-data?tab=structured&assetClass=RMBS&new=1
  const query = new URLSearchParams(location.search);
  const structuredAssetClass = query.get('assetClass') || undefined;
  const structuredOpenNew = query.get('new') === '1';

  useEffect(() => {
    if (location.pathname === '/compliance-rules' || location.pathname === '/reference-data/compliance') {
      setActiveTab('compliance');
    }
    const tabParam = new URLSearchParams(location.search).get('tab');
    if (tabParam === 'structured') {
      setActiveTab('structured');
    }
    if (tabParam === 'securities') {
      setActiveTab('securities');
    }
    if (tabParam === 'credit-loans') {
      setActiveTab('credit-loans');
    }
    if (tabParam === 'derivatives') {
      setActiveTab('derivatives');
    }
  }, [location.pathname, location.search]);

  if (isRetail) {
    return <RetailReferenceScreen />;
  }

  const tabs: Array<{
    id: TabType;
    label: string;
    description: string;
    icon: React.ReactNode;
    badge: string;
    tone: 'cyan' | 'violet' | 'emerald';
  }> = [
    {
      id: 'portfolios',
      label: 'Portfolios',
      description: 'Portfolio master, strategies, codes and NAV context.',
      icon: <Briefcase />,
      badge: 'Book',
      tone: 'cyan',
    },
    {
      id: 'groups',
      label: 'Portfolio Groups',
      description: 'Hierarchy, aggregation groups and oversight scopes.',
      icon: <Layers />,
      badge: 'Hierarchy',
      tone: 'cyan',
    },
    {
      id: 'securities',
      label: 'Security Master',
      description: 'SMF identifiers, taxonomy, terms and security metadata.',
      icon: <Lock />,
      badge: 'SMF',
      tone: 'violet',
    },
    {
      id: 'structured',
      label: 'Structured Products',
      description: 'Define securitized deals & tranches (RMBS, CMBS, CLO) once for trading.',
      icon: <Boxes />,
      badge: 'Deals',
      tone: 'violet',
    },
    {
      id: 'derivatives',
      label: 'Derivatives',
      description: 'Define listed derivative contracts (options) once for trading.',
      icon: <Sigma />,
      badge: 'Contracts',
      tone: 'violet',
    },
    {
      id: 'credit-loans',
      label: 'Private Credit / LL',
      description: 'Search and define governed private-credit and leveraged-loan reference records.',
      icon: <Database />,
      badge: 'Loans',
      tone: 'violet',
    },
    {
      id: 'compliance',
      label: 'Compliance Rules',
      description: 'Rule library, scopes, thresholds and pre-clear controls.',
      icon: <ShieldCheck />,
      badge: 'Rules',
      tone: 'emerald',
    },
  ];
  const activeMeta = tabs.find(tab => tab.id === activeTab) || tabs[0];

  return (
    <WorkspaceShell>
      <WorkspaceHero>
        <HeroTop>
          <div>
            <Eyebrow $tone="cyan">Reference Workspace</Eyebrow>
            <HeroTitle>Referential Master</HeroTitle>
            <HeroSubtitle>
              Central master-data workspace for portfolios, portfolio groups, security master and compliance rules.
              This is the source layer that tickets, IBOR, analytics and middle office consume.
            </HeroSubtitle>
          </div>
          <AxiomStatusBadge $tone="cyan">
            <Database className="h-3 w-3" />
            Master Feed
          </AxiomStatusBadge>
        </HeroTop>
        <AxiomMetricGrid style={{ marginTop: '1rem' }}>
          <AxiomMetricCard label="Hierarchy" value="Portfolio -> Group" subtext="Aggregation and governance scopes" icon={<FolderTree />} tone="cyan" />
          <AxiomMetricCard label="Instrument" value="Security Master" subtext="Identifiers, taxonomy and terms" icon={<Database />} tone="violet" />
          <AxiomMetricCard label="Controls" value="Compliance Rules" subtext="Rule scopes and thresholds" icon={<ShieldCheck />} tone="emerald" />
        </AxiomMetricGrid>
      </WorkspaceHero>

      <WorkspaceBody $railCollapsed={railCollapsed}>
        <WorkspaceRail $collapsed={railCollapsed}>
          <WorkspaceRailToggle collapsed={railCollapsed} onToggle={() => setRailCollapsed(prev => !prev)} />
          {railCollapsed ? (
            <CollapsedRail>
              <CollapsedRailSection>
                {tabs.map(tab => (
                  <CollapsedRailItem
                    key={tab.id}
                    $active={activeTab === tab.id}
                    $tone={tab.tone}
                    aria-label={tab.label}
                    title={`${tab.label} - ${tab.description}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.icon}
                  </CollapsedRailItem>
                ))}
              </CollapsedRailSection>
            </CollapsedRail>
          ) : (
            <RailSection>
              <RailSectionLabel>Master Data</RailSectionLabel>
              {tabs.map(tab => (
                <RailItem
                  key={tab.id}
                  $active={activeTab === tab.id}
                  $tone={tab.tone}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.icon}
                  <div>
                    <RailItemLabel>{tab.label}</RailItemLabel>
                    <RailItemDescription>{tab.description}</RailItemDescription>
                  </div>
                </RailItem>
              ))}
            </RailSection>
          )}
        </WorkspaceRail>

        <WorkspaceMain>
          <WorkspacePanel>
            <PanelHeader>
              <div>
                <PanelTitle>{activeMeta.label}</PanelTitle>
                <PanelSubtitle>{activeMeta.description}</PanelSubtitle>
              </div>
              <AxiomStatusBadge $tone="slate">{activeMeta.badge}</AxiomStatusBadge>
            </PanelHeader>
            <PanelBody>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {activeTab === 'portfolios' && <PortfolioManagementScreen />}
                  {activeTab === 'groups' && <PortfolioGroupsScreen embedded />}
                  {activeTab === 'securities' && <SecurityManagementScreen />}
                  {activeTab === 'structured' && (
                    <StructuredProductsScreen
                      initialAssetClass={structuredAssetClass}
                      openNewOnMount={structuredOpenNew}
                    />
                  )}
                  {activeTab === 'derivatives' && (
                    <StructuredProductsScreen
                      classKeys={['OPTIONS', 'FUTURES']}
                      initialAssetClass={structuredAssetClass || 'OPTIONS'}
                      openNewOnMount={structuredOpenNew}
                    />
                  )}
                  {activeTab === 'credit-loans' && (
                    <CreditLoanReferenceWorkspace
                      initialAssetClass={structuredAssetClass}
                      openNewOnMount={structuredOpenNew}
                      initialQuery={query.get('query') || ''}
                    />
                  )}
                  {activeTab === 'compliance' && <ComplianceRulesScreen />}
                </motion.div>
              </AnimatePresence>
            </PanelBody>
          </WorkspacePanel>
        </WorkspaceMain>
      </WorkspaceBody>
    </WorkspaceShell>
  );
};

export default ReferenceDataScreen;
